import React, { useState } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Button, Box, Input, Text, useToast } from '@chakra-ui/react';
import c from '../../constant';
import './style.css';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { gql, useMutation } from '@apollo/client';

const IMPORT_EMPLOYEES = gql`
	mutation excelImportEmployees($employees: [ImportEmployeeInput!]!) {
		excelImportEmployees(employees: $employees)
	}
`;

function ExcelEmployeeImport({ isOpen = false, onClose = () => null }) {
	const { t } = useTranslation();
	const toast = useToast();

	const [file, setFile] = useState(null);
	const [excelData, setExcelData] = useState([]);

	const [importEmployees, { loading }] = useMutation(IMPORT_EMPLOYEES);

	const khmerDigitMap = {
		'០': '0',
		'១': '1',
		'២': '2',
		'៣': '3',
		'៤': '4',
		'៥': '5',
		'៦': '6',
		'៧': '7',
		'៨': '8',
		'៩': '9',
	};

	const khmerMonthMap = {
		មករា: 1,
		កុម្ភៈ: 2,
		មីនា: 3,
		មេសា: 4,
		ឧសភា: 5,
		មិថុនា: 6,
		កក្កដា: 7,
		សីហា: 8,
		កញ្ញា: 9,
		តុលា: 10,
		វិច្ឆិកា: 11,
		វិច្ឋិកា: 11,
		ធ្នូ: 12,
	};

	const convertKhmerDigits = (value) => {
		return String(value).replace(/[០-៩]/g, (digit) => khmerDigitMap[digit]);
	};

	const parseExcelDate = (value) => {
		if (!value) {
			return null;
		}

		if (value instanceof Date) {
			if (isNaN(value.getTime())) {
				return null;
			}

			return value.toISOString();
		}

		if (typeof value === 'number') {
			const parsed = XLSX.SSF.parse_date_code(value);

			if (!parsed) {
				return null;
			}

			const date = new Date(Date.UTC(parsed.y, parsed.m - 1, parsed.d));

			return date.toISOString();
		}

		const stringValue = String(value).trim();

		const parts = stringValue.split(/\s+/);

		if (parts.length === 3) {
			const day = Number(convertKhmerDigits(parts[0]));

			const month = khmerMonthMap[parts[1]];

			const year = Number(convertKhmerDigits(parts[2]));

			if (day && month && year) {
				const date = new Date(Date.UTC(year, month - 1, day));

				if (!isNaN(date.getTime())) {
					return date.toISOString();
				}
			}
		}

		const normalDate = new Date(stringValue);

		if (!isNaN(normalDate.getTime())) {
			return normalDate.toISOString();
		}

		console.log('Unable to parse date:', value);

		return null;
	};

	const normalizeRow = (row) => {
		const normalizedRow = {};

		Object.entries(row).forEach(([key, value]) => {
			const cleanKey = key.replace(/\r?\n/g, '').trim();

			normalizedRow[cleanKey] = value;
		});

		return normalizedRow;
	};

	const getPositions = (row) => {
		const positions = [];

		const positionColumns = [
			{
				column: 'អគ្គនាយក',
				position: '1',
			},
			{
				column: 'អគ្គនាយករង',
				position: '2',
			},
			{
				column: 'ប្រធាននាយកដ្ឋាន',
				position: '3',
			},
			{
				column: 'អនុប្រធាននាយកដ្ឋាន',
				position: '4',
			},
			{
				column: 'ប្រធានការិយាល័យ',
				position: '5',
			},
			{
				column: 'អនុប្រធានការិយាល័យ',
				position: '6',
			},
			{
				column: 'មន្រ្តីលក្ខន្តិកៈ',
				position: '7',
			},
		];

		positionColumns.forEach(({ column, position }) => {
			const value = row[column];

			if (!value) {
				return;
			}

			const dateOfLastPosition = parseExcelDate(value);

			positions.push({
				position,
				dateOfLastPosition,
			});
		});

		return positions;
	};

	const getGender = (value) => {
		const gender = value.trim().toLowerCase();
		if (gender === 'ប្រុស' || gender === 'male') {
			return 'male';
		}
		if (gender === 'ស្រី' || gender === 'female') {
			return 'female';
		}

		return null;
	};

	const handleFileChange = (event) => {
		const selectedFile = event.target.files?.[0];

		if (!selectedFile) {
			return;
		}

		setFile(selectedFile);

		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				const workbook = XLSX.read(e.target.result, {
					type: 'binary',
					cellDates: true,
				});

				const sheetName = workbook.SheetNames[0];

				const worksheet = workbook.Sheets[sheetName];

				const rawRows = XLSX.utils.sheet_to_json(worksheet, {
					defval: '',
				});

				const rows = rawRows.map(normalizeRow);

				console.log('Excel headers:', Object.keys(rows[0] || {}));

				const employees = rows
					.map((row) => {
						return {
							govtNumber: row['អត្តលេខ'] || null,

							fullName: row['ឈ្មោះ'] || null,

							fullNameLatin: row['NAME IN LATIN'] || null,

							gender: getGender(row['ភេទ'] || null),

							department: row['នាយកដ្ឋាន'] || null,

							office: row['ការិយាល័យ'] || null,

							dateOfEmployment: parseExcelDate(row['ថ្ងៃចូលបម្រើការងារ']),

							positions: getPositions(row),
						};
					})
					.filter((employee) => employee.fullNameLatin);

				console.log('Mapped employees:', employees);

				console.table(
					employees.map((employee) => ({
						name: employee.fullNameLatin,

						dateOfEmployment: employee.dateOfEmployment,

						positions: JSON.stringify(employee.positions),
					})),
				);

				setExcelData(employees);
			} catch (error) {
				console.error('Excel read error:', error);

				toast({
					title: t('Failed to read Excel file'),
					description: error?.message,
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		};

		reader.readAsBinaryString(selectedFile);
	};

	const handleImport = async () => {
		try {
			await importEmployees({
				variables: {
					employees: excelData,
				},
			});

			toast({
				title: t('Import successful'),
				status: 'success',
				isClosable: true,
				position: 'bottom-right',
			});

			setFile(null);
			setExcelData([]);

			onClose();
		} catch (error) {
			console.error(error);

			toast({
				title: t('Import failed'),
				description: error?.message,
				status: 'error',
				isClosable: true,
				position: 'bottom-right',
			});
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md">
			<ModalOverlay />

			<ModalContent pb="4" borderRadius={c.borderRadius}>
				<ModalHeader>{t('Import Employee Data')}</ModalHeader>

				<ModalCloseButton />

				<ModalBody>
					<Box w="100%" pt="16px" pb="16px">
						<Input type="file" accept=".xlsx,.xls" onChange={handleFileChange} p="4px" />

						{file && <Text mt="3">{file.name}</Text>}

						{excelData.length > 0 && <Text mt="2">{excelData.length} rows found</Text>}

						<Button mt="4" w="100%" colorScheme="blue" isLoading={loading} isDisabled={excelData.length === 0} onClick={handleImport}>
							{t('Import')}
						</Button>
					</Box>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default ExcelEmployeeImport;
